/**
 * Resolved CUPA-conflict decisions, newest first. Read-mostly: each row shows
 * what was decided, by whom, and what it changed; only the newest non-undone
 * decision per conflict offers Undo. Undo is one click (it is the safety net
 * for a misclick — no confirmation, no reason). Undone rows stay listed as
 * history with an "Undone" chip.
 */
import { useApolloClient, useMutation } from "@apollo/client";
import { Box, Button, Link as MuiLink, Stack, Tooltip, Typography } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { StatusChip } from "components/StatusChip";
import {
  StaffCersCupaConflictResolutionsQuery,
  StaffCersCupaConflictResolutionsQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { AddressLine } from "../Facilities/MatchCells";
import { STAFF_CERS_CUPA_CONFLICT_RESOLUTIONS, UNDO_CERS_CUPA_CONFLICT_RESOLUTION } from "./api";
import { changeDisplay, decisionChip, ResolutionRow } from "./resolutionRow";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const getItems = (data: StaffCersCupaConflictResolutionsQuery) =>
  data.staffCersCupaConflictResolutions.items;
const getCount = (data: StaffCersCupaConflictResolutionsQuery) =>
  data.staffCersCupaConflictResolutions.count;
const getRowId = (row: ResolutionRow) => row.id;
const getRowHeight = () => "auto" as const;

function ResolvedFacilityCell({ row }: { row: ResolutionRow }) {
  const { facility } = row;
  return (
    <Box sx={{ py: 0.75 }}>
      <MuiLink
        component={Link}
        to={`/o/${facility.tenantId}/facilities/${facility.id}/profile`}
        target="_blank"
        rel="noopener noreferrer"
        variant="body1"
      >
        {facility.name}
      </MuiLink>
      <AddressLine parts={[facility.streetAddress1, facility.city, facility.state, facility.zip]} />
    </Box>
  );
}

export function ResolvedGrid({
  onUndone,
}: {
  /** Lets the parent roll back its session progress state for the conflict. */
  onUndone?: (cersFacilityId: string) => void;
}) {
  const alerts = useAlerts();
  const client = useApolloClient();
  const [undoingIds, setUndoingIds] = useState<ReadonlySet<string>>(new Set());
  const [undoResolution] = useMutation(UNDO_CERS_CUPA_CONFLICT_RESOLUTION);

  const handleUndo = useCallback(
    (row: ResolutionRow) => {
      setUndoingIds((prev) => new Set(prev).add(row.id));
      void undoResolution({ variables: { resolutionId: row.id } })
        .then(() => {
          alerts.success("Decision undone — the conflict will reappear if it still applies");
          onUndone?.(row.cersFacility.cersId);
          void client.refetchQueries({
            include: ["StaffCersCupaConflictResolutions", "StaffCersCupaConflicts"],
          });
        })
        .catch((e: unknown) => {
          alerts.error(
            `Couldn't undo ${row.facility.name}: ${e instanceof Error ? e.message : String(e)}`
          );
        })
        .finally(() =>
          setUndoingIds((prev) => {
            const next = new Set(prev);
            next.delete(row.id);
            return next;
          })
        );
    },
    [undoResolution, alerts, client, onUndone]
  );

  const columns = useMemo<OmnisearchGridColDef<ResolutionRow>[]>(
    () => [
      {
        field: "facility",
        headerName: "Facility",
        flex: 1.1,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => <ResolvedFacilityCell row={row} />,
      },
      {
        field: "tenant",
        headerName: "Organization",
        flex: 0.6,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.facility.tenant?.name ?? "",
      },
      {
        field: "decision",
        headerName: "Decision",
        flex: 0.7,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const chip = decisionChip(row.resolution);
          return (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ py: 1 }}>
              <StatusChip color={chip.color} label={chip.label} />
              {row.undoneAt && (
                <Tooltip
                  title={`Undone by ${row.undoneBy?.email ?? "unknown"} on ${new Date(
                    row.undoneAt
                  ).toLocaleString()}`}
                >
                  <span>
                    <StatusChip color="default" label="Undone" />
                  </span>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        field: "change",
        headerName: "Change",
        flex: 1,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => changeDisplay(row),
      },
      {
        field: "note",
        headerName: "Reason",
        flex: 1,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.note ?? "",
      },
      {
        field: "resolvedBy",
        headerName: "Resolved by",
        flex: 0.9,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box sx={{ py: 0.75 }}>
            <Typography variant="body2">{row.resolvedBy?.email ?? "unknown"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(row.resolvedAt).toLocaleString()}
            </Typography>
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (row.isUndoable) {
            return (
              <Button
                size="small"
                disabled={undoingIds.has(row.id)}
                onClick={() => handleUndo(row)}
              >
                Undo
              </Button>
            );
          }
          if (!row.undoneAt) {
            return (
              <Typography variant="caption" color="text.secondary">
                Superseded
              </Typography>
            );
          }
          return null;
        },
      },
    ],
    [handleUndo, undoingIds]
  );

  const buildVariables = useCallback(
    (
      currentOmnisearch: string,
      pagination: { page: number; pageSize: number },
      _sort: GridSortModel
    ): StaffCersCupaConflictResolutionsQueryVariables => {
      const text = currentOmnisearch.trim();
      return {
        search: text.length > 0 ? text : undefined,
        page: pagination.page,
        pageSize: pagination.pageSize,
      };
    },
    []
  );

  return (
    <OmnisearchDataGridV2
      query={STAFF_CERS_CUPA_CONFLICT_RESOLUTIONS}
      getItems={getItems}
      getCount={getCount}
      getRowId={getRowId}
      persistenceKey="staff-cers-cupa-conflict-resolutions-table"
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      columns={columns}
      buildVariables={buildVariables}
      getRowHeight={getRowHeight}
      noRowsMessage="No resolved conflicts yet — decisions made from the Open view appear here."
    />
  );
}
